import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { Bar, BarChart, XAxis, YAxis } from "recharts"

import type { DashboardUseCase } from "../../types/use-case.types"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

import { ENTERPRISE_FUNCTIONS } from "../../types/use-case.types"

type FunctionCoverageChartProps = {
  useCases: DashboardUseCase[]
}

export function FunctionCoverageChart({ useCases }: FunctionCoverageChartProps) {
  const { t } = useTranslation()

  const chartConfig = useMemo(
    () =>
      ({
        count: { label: t("useCases.dashboard.charts.useCases"), color: "hsl(var(--chart-1))" },
      }) satisfies ChartConfig,
    [t]
  )

  const data = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const fn of ENTERPRISE_FUNCTIONS) {
      counts[fn] = 0
    }
    for (const uc of useCases) {
      for (const val of uc.efSel) {
        if (val in counts) counts[val]++
      }
    }
    return Object.entries(counts)
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({
        name: name.length > 20 ? name.slice(0, 17) + "..." : name,
        fullName: name,
        count,
      }))
  }, [useCases])

  if (data.length === 0) return null

  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("useCases.dashboard.charts.functionCoverage")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer
          className="w-full"
          config={chartConfig}
          style={{ height: Math.max(200, data.length * 28) }}
        >
          <BarChart
            data={data}
            layout="vertical"
            margin={{ left: 10, right: 30, top: 5, bottom: 5 }}
          >
            <XAxis tick={{ fontSize: 11 }} type="number" />
            <YAxis dataKey="name" tick={{ fontSize: 11 }} type="category" width={140} />
            <ChartTooltip
              content={<ChartTooltipContent />}
              labelFormatter={(_label, payload) => payload[0]?.payload?.fullName ?? _label}
            />
            <Bar
              dataKey="count"
              fill="hsl(var(--chart-1))"
              name={t("useCases.dashboard.charts.useCases")}
              radius={[0, 4, 4, 0]}
              stroke="hsl(var(--foreground))"
              strokeWidth={1}
            />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
